// Cancel a work item, and say why.
//
// The behaviour lives in CancelWithReasonDialog, which expense reports and
// payroll runs use too — this file is now just the work vocabulary and copy.
//
// The reason stays OPTIONAL here. Cancelling a work item already worked without
// one, and turning a working action into a form people must fill in would only
// teach them to pick the first option to get past it.
//
// When the item has open sub-items the dialog says HOW MANY and offers to stop
// them too (on by default). Cancelling a parent used to leave its breakdown
// open — still counting as somebody's load, still overdue, still reminding.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CancelWithReasonDialog } from '@/features/shared/components/CancelWithReasonDialog';
import { Checkbox } from '@/features/shared/components/ui/checkbox';
import { WORK_CANCEL_REASONS, type WorkCancelReason } from '@/services/workService';

export const CancelWorkDialog: React.FC<{
  open: boolean;
  busy?: boolean;
  /** Open sub-items at every depth (detail.subitems.open_descendants). 0/absent hides the option. */
  openSubitems?: number;
  onClose: () => void;
  onConfirm: (opts: { reason?: WorkCancelReason; note?: string; cascade?: boolean }) => void;
}> = ({ open, busy, openSubitems = 0, onClose, onConfirm }) => {
  const { t } = useTranslation('work');
  const [cascade, setCascade] = useState(true);
  // A fresh decision every time the dialog opens — never a stale choice.
  useEffect(() => { if (open) setCascade(true); }, [open]);
  const hasSubitems = openSubitems > 0;

  return (
    <CancelWithReasonDialog
      open={open}
      busy={busy}
      onClose={onClose}
      onConfirm={({ reason, note }) =>
        onConfirm({ reason: reason as WorkCancelReason | undefined, note, cascade: hasSubitems && cascade })
      }
      reasons={WORK_CANCEL_REASONS.map((r) => ({
        code: r.code,
        label: t(`cancel_reason_${r.code}`, { defaultValue: r.label }),
      }))}
      title={t('cancel_item_title', { defaultValue: 'Cancel this work item' })}
      description={t('cancel_item_help', {
        defaultValue:
          'The item stays on the record as cancelled — nothing is deleted. A reason helps everyone see later why work was dropped.',
      })}
      confirmLabel={t('cancel_item', { defaultValue: 'Cancel item' })}
      keepLabel={t('keep', { defaultValue: 'Keep it' })}
      notePlaceholder={t('cancel_note_ph', { defaultValue: 'e.g. merged into WI-214' })}
    >
      {hasSubitems && (
        <label className="flex items-start gap-2 text-sm text-foreground" htmlFor="cancel-cascade">
          <Checkbox id="cancel-cascade" checked={cascade} disabled={busy}
            onCheckedChange={(v) => setCascade(v === true)} className="mt-0.5" />
          <span>
            {t('cancel_cascade', {
              count: openSubitems,
              defaultValue: 'Also cancel its {{count}} open sub-items, with the same reason',
            })}
            <span className="block text-xs text-muted-foreground">
              {t('cancel_cascade_help', {
                defaultValue: 'Left unticked, they stay open and keep counting as their owners’ work.',
              })}
            </span>
          </span>
        </label>
      )}
    </CancelWithReasonDialog>
  );
};

export default CancelWorkDialog;
